/**
 * Per-session activity for the sidebar's status glyphs and the document
 * title's unread dot (Stage A):
 *
 *   'writing' — a turn is streaming for that session right now;
 *   'unread'  — a turn finished while the session's route was NOT mounted
 *               (there is new content the user hasn't seen);
 *   'seen'    — the steady state (also the implicit default).
 *
 * Wired from the stream controller (writing on start; terminal → unread or
 * seen depending on whether the session's route is mounted) and from
 * SessionPage (mounting a session's route marks it seen and registers it as
 * the one being viewed). 'unread' persists to localStorage so a finished
 * background reply still shows its dot after a reload; 'writing' never
 * persists: the turn keeps running on the server through a reload
 * (2026-09-07), and the page that mounts it attaches to it and marks it
 * writing again from what the server sends (streamController.attachTurn).
 *
 * `products` maps session → owning product where known (read off the query
 * cache at stream start) so the product switcher can show its tiny "other
 * products have activity" dot.
 */

import { create } from 'zustand';

import type { Ulid } from '../api/types';

export type SessionActivityStatus = 'writing' | 'unread' | 'seen';

const STORAGE_KEY = 'loremap.sessions.unread';

function loadUnread(): Ulid[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is Ulid => typeof v === 'string');
  } catch {
    return [];
  }
}

function persistUnread(statuses: Record<Ulid, SessionActivityStatus>): void {
  if (typeof window === 'undefined') return;
  try {
    const unread = Object.entries(statuses)
      .filter(([, status]) => status === 'unread')
      .map(([sid]) => sid);
    if (unread.length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(unread));
    }
  } catch {
    // Storage full/blocked: the dot just does not survive a reload.
  }
}

export interface SessionActivityState {
  statuses: Record<Ulid, SessionActivityStatus>;
  /** session → owning product (where known) for cross-product dots */
  products: Record<Ulid, Ulid>;
  /** the session whose route is currently mounted (SessionPage registers it) */
  viewingSessionId: Ulid | null;

  /** SessionPage mount/unmount. Mounting also clears that session's unread. */
  setViewing: (sessionId: Ulid | null) => void;
  /** Teach the store which product owns a session (route mounts know it). */
  recordProduct: (sessionId: Ulid, productId: Ulid) => void;
  markWriting: (sessionId: Ulid, productId?: Ulid) => void;
  /**
   * A stream settled (terminal frame, stop, drop). Becomes 'unread' unless
   * the session's route is mounted right now; `silent` (logout teardown)
   * always lands 'seen' — no dot owed across accounts.
   */
  markTerminal: (sessionId: Ulid, opts?: { silent?: boolean }) => void;
  markSeen: (sessionId: Ulid) => void;
  reset: () => void;
}

function hydrateInitial(): Record<Ulid, SessionActivityStatus> {
  const statuses: Record<Ulid, SessionActivityStatus> = {};
  for (const sid of loadUnread()) statuses[sid] = 'unread';
  return statuses;
}

export const useSessionActivity = create<SessionActivityState>((set, get) => ({
  statuses: hydrateInitial(),
  products: {},
  viewingSessionId: null,

  setViewing(sessionId) {
    set({ viewingSessionId: sessionId });
    if (sessionId !== null) get().markSeen(sessionId);
  },

  recordProduct(sessionId, productId) {
    set((s) =>
      s.products[sessionId] === productId
        ? s
        : { products: { ...s.products, [sessionId]: productId } },
    );
  },

  markWriting(sessionId, productId) {
    set((s) => {
      const statuses = { ...s.statuses, [sessionId]: 'writing' as const };
      persistUnread(statuses);
      return {
        statuses,
        products:
          productId !== undefined ? { ...s.products, [sessionId]: productId } : s.products,
      };
    });
  },

  markTerminal(sessionId, opts) {
    // "Seen" needs the route mounted AND the document actually visible — a
    // reply finishing in a hidden tab still earns its dot.
    const viewing =
      get().viewingSessionId === sessionId &&
      (typeof document === 'undefined' || document.visibilityState !== 'hidden');
    const next: SessionActivityStatus = opts?.silent === true || viewing ? 'seen' : 'unread';
    set((s) => {
      const statuses = { ...s.statuses, [sessionId]: next };
      persistUnread(statuses);
      return { statuses };
    });
  },

  markSeen(sessionId) {
    set((s) => {
      // Round 9 (owner: "the in progress indicator from the sidebar is
      // gone"): seen clears the UNREAD dot and nothing else. It used to
      // flatten 'writing' too, and the commonest flow hit it every time — a
      // first question creates the session, markWriting fires, THEN the
      // route mounts and its markSeen killed the spinner mid-stream. A
      // stream ends through markTerminal, never through being looked at.
      if (s.statuses[sessionId] !== 'unread') return s;
      const statuses = { ...s.statuses, [sessionId]: 'seen' as const };
      persistUnread(statuses);
      return { statuses };
    });
  },

  reset() {
    set({ statuses: {}, products: {}, viewingSessionId: null });
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* noop */
      }
    }
  },
}));

/** Any session anywhere carrying an unread dot (the document-title dot). */
export function selectAnyUnread(s: SessionActivityState): boolean {
  return Object.values(s.statuses).includes('unread');
}

/**
 * True when a session OUTSIDE `productId` is writing or unread — the product
 * switcher's tiny attention dot. Sessions whose product is unknown never
 * count (no false alarms).
 */
export function hasOtherProductActivity(
  s: Pick<SessionActivityState, 'statuses' | 'products'>,
  productId: Ulid | null,
): boolean {
  return Object.entries(s.statuses).some(([sid, status]) => {
    if (status !== 'writing' && status !== 'unread') return false;
    const owner = s.products[sid];
    return owner !== undefined && owner !== productId;
  });
}
